// apps/admin/src/i18n/useTranslation.ts
//
// Phase 8.1 Wave 10 Task 46 — Tiny `useTranslation` hook for the admin
// portal. Replaces a hard `react-i18next` dependency (deliberately not
// added in this wave — see config.ts header).
//
// vg-binding: P8.D-165 — i18n EN/VI/FR. Hook returns a `t()` function that
//             re-renders the calling component when the active locale flips.
//
// Behaviour mirrors react-i18next's `useTranslation(ns)` enough for our
// pages:
//   const { t, locale } = useTranslation('admin');
//   t('errors.notFound.title') // -> "Không tìm thấy trang"
//
// Implementation: `useSyncExternalStore` subscribes to i18next's
// `languageChanged` event so React re-renders consumers without us having
// to wire a context provider.

import { useSyncExternalStore, useMemo, useCallback } from 'react';

import { i18n, type SupportedLocale } from './config.js';

type Subscriber = () => void;

function subscribe(notify: Subscriber): () => void {
  i18n.on('languageChanged', notify);
  return () => {
    i18n.off('languageChanged', notify);
  };
}

function getLocaleSnapshot(): SupportedLocale {
  const lng = (i18n.language || 'vi') as string;
  if (lng === 'vi' || lng === 'en' || lng === 'fr') return lng;
  return 'vi';
}

// Resource-version store — bumps whenever a namespace bundle registers at
// runtime. Lazy namespaces (e.g. `p83`, loaded via `ensureP83Loaded()` in a
// route `useEffect`) call `addResourceBundle` AFTER first paint; i18next emits
// `added` on its ResourceStore (and `loaded` on the instance). Without
// subscribing here, `useSyncExternalStore` never sees a snapshot change, so
// components that read a not-yet-loaded namespace stay stuck on the raw key
// until an unrelated re-render (bug: /admin/audit-log header showed
// `reports.auditLogs.*` literally). Tracking a version counter forces the
// re-render the moment the bundle lands.
let resourceVersion = 0;
function subscribeResources(notify: Subscriber): () => void {
  const bump = (): void => {
    resourceVersion += 1;
    notify();
  };
  i18n.on('loaded', bump);
  i18n.store?.on?.('added', bump);
  return () => {
    i18n.off('loaded', bump);
    i18n.store?.off?.('added', bump);
  };
}

function getResourceVersion(): number {
  return resourceVersion;
}

export interface UseTranslationResult {
  /** Bound translator. `ns` defaults to `'admin'`. */
  t: (key: string, vars?: Record<string, unknown>) => string;
  /** Active locale — re-rendered when changed. */
  locale: SupportedLocale;
  /** Pass-through to `i18n.changeLanguage`. */
  changeLanguage: (next: SupportedLocale) => Promise<void>;
}

/**
 * React hook returning a translator bound to the given namespace (default
 * `'admin'`). Re-renders on locale change.
 */
export function useTranslation(
  ns: string = 'admin',
): UseTranslationResult {
  const locale = useSyncExternalStore(
    subscribe,
    getLocaleSnapshot,
    getLocaleSnapshot,
  );

  // Re-render when a lazily-loaded namespace bundle registers at runtime.
  const resourceVersion = useSyncExternalStore(
    subscribeResources,
    getResourceVersion,
    getResourceVersion,
  );

  const t = useCallback(
    (key: string, vars?: Record<string, unknown>) => {
      // `i18n.t` is typed loosely upstream; cast the return to string to
      // shield callers from `string | object` union noise.
      return i18n.t(key, { ns, ...vars }) as unknown as string;
    },
    // `locale` is intentionally in the dep list so `t` gets a fresh identity
    // on every language change. Consumers that memoize t()-derived values
    // (table columns, tab defs, option lists via useMemo([t])) then rebuild
    // automatically — without it, only direct t() calls re-render on switch
    // while memoized structures keep the previous locale until a refresh.
    // `resourceVersion` is included so the same rebuild happens when a lazy
    // namespace bundle finishes loading after first paint.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ns, locale, resourceVersion],
  );

  const changeLanguage = useCallback(async (next: SupportedLocale) => {
    await i18n.changeLanguage(next);
  }, []);

  return useMemo(
    () => ({ t, locale, changeLanguage }),
    [t, locale, changeLanguage],
  );
}
